// Compact read-only panel that shows the last visit's key data side-by-side
// with the current editing form. Includes a per-medicine diff badge: NEW /
// SAME / CHANGED (dose or frequency) vs the current draft.

import React, { useMemo } from "react";
import { Pill, Activity, ClipboardList, Calendar, ArrowRight, GitCompareArrows, FlaskConical, Copy, TrendingUp, TrendingDown, Minus } from "lucide-react";
import MiniSparkline from "./MiniSparkline";
import { canonicalKey } from "./TrendChip";

function medKey(m) {
  // Normalise: prefer generic_name; fall back to brand.
  return (m.generic_name || m.brand_name || "").toLowerCase().replace(/\s+/g, " ").trim();
}

function medStatus(prev, current) {
  const cur = current.find((c) => medKey(c) === medKey(prev));
  if (!cur) return { kind: "removed", label: "STOPPED", cls: "bg-red-100 text-red-700" };
  const changed = (
    (String(prev.dose || "").trim() !== String(cur.dose || "").trim()) ||
    (String(prev.frequency || "").trim() !== String(cur.frequency || "").trim()) ||
    (String(prev.duration || "").trim() !== String(cur.duration || "").trim())
  );
  return changed
    ? { kind: "changed", label: "CHANGED", cls: "bg-amber-100 text-amber-800", cur }
    : { kind: "same", label: "SAME", cls: "bg-emerald-100 text-emerald-700", cur };
}

function newInCurrent(prevMeds, curMeds) {
  const prevKeys = new Set(prevMeds.map(medKey).filter(Boolean));
  return curMeds.filter((c) => medKey(c) && !prevKeys.has(medKey(c)));
}

export default function LastVisitPanel({ visit, currentMedicines = [], currentDiagnosis, currentVitals, history = [], latestLabs = {}, onCopyNextTests }) {
  const prevMeds = visit?.medicines || [];
  const newMeds = useMemo(() => newInCurrent(prevMeds, currentMedicines), [prevMeds, currentMedicines]);

  // Build sparkline series from the last 5 visits (oldest → newest so the
  // sparkline reads left-to-right chronologically).
  const series = useMemo(() => {
    const chrono = [...(history || [])].reverse().slice(-5); // history is newest-first
    const num = (x) => {
      const n = parseFloat(String(x ?? "").replace(",", ""));
      return Number.isFinite(n) ? n : null;
    };
    const collect = (kind) => {
      const out = [];
      for (const v of chrono) {
        let val = null;
        if (kind === "sbp") val = num(v.vitals?.sbp);
        else if (kind === "weight") val = num(v.vitals?.weight);
        else if (kind === "bmi") val = num(v.vitals?.bmi);
        else {
          // Investigations may be inline in the visit doc or under `investigations`
          const invs = v.investigations || v.investigations_full || [];
          const target = { hba1c: "hba1c", ldl: "ldl", creatinine: "creatinine" }[kind];
          const row = invs.find((i) => canonicalKey(i.test_name) === target);
          if (row) val = num(row.result);
        }
        if (val !== null) out.push({ v: val, d: v.visit_date });
      }
      return out;
    };
    return {
      sbp: collect("sbp"),
      weight: collect("weight"),
      bmi: collect("bmi"),
      hba1c: collect("hba1c"),
      ldl: collect("ldl"),
      creatinine: collect("creatinine"),
    };
  }, [history]);

  if (!visit) {
    return (
      <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded-md p-4">
        No previous visit on file yet.
      </div>
    );
  }
  const vitals = visit.vitals || {};

  return (
    <div className="border border-slate-200 rounded-md bg-white text-xs" data-testid="last-visit-panel">
      <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GitCompareArrows size={13} className="text-pink-600" />
          <span className="font-semibold text-slate-900">Last visit</span>
          <span className="text-slate-500 tabular">
            {visit.visit_date && new Date(visit.visit_date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" })}
          </span>
        </div>
        <span className="text-[9px] uppercase tracking-wider font-bold text-slate-400">Read-only</span>
      </div>

      <div className="p-3 space-y-3">
        {/* Sparkline strip — trends across last ≤5 visits */}
        {history.length >= 2 && (
          <div className="grid grid-cols-2 gap-1.5" data-testid="spark-strip">
            {series.sbp.length >= 2 && <MiniSparkline label="SBP" unit="mmHg" points={series.sbp} betterDown />}
            {series.weight.length >= 2 && <MiniSparkline label="Weight" unit="kg" points={series.weight} betterDown />}
            {series.hba1c.length >= 2 && <MiniSparkline label="HbA1c" unit="%" points={series.hba1c} betterDown />}
            {series.ldl.length >= 2 && <MiniSparkline label="LDL" unit="mg/dL" points={series.ldl} betterDown />}
            {series.creatinine.length >= 2 && <MiniSparkline label="Creatinine" unit="mg/dL" points={series.creatinine} betterDown />}
            {series.bmi.length >= 2 && series.weight.length < 2 && <MiniSparkline label="BMI" unit="" points={series.bmi} betterDown />}
          </div>
        )}
        {visit.diagnosis?.primary && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5 flex items-center gap-1">
              <ClipboardList size={10} /> Diagnosis
            </div>
            <div className="text-slate-800">
              <strong>{visit.diagnosis.primary}</strong>
              {visit.diagnosis.status && <span className="text-slate-500"> · {visit.diagnosis.status}</span>}
            </div>
            {currentDiagnosis?.primary && currentDiagnosis.primary !== visit.diagnosis.primary && (
              <div className="text-[10px] text-amber-700 mt-0.5 flex items-center gap-1">
                <ArrowRight size={10} /> Now: {currentDiagnosis.primary}
              </div>
            )}
          </div>
        )}

        <div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5 flex items-center gap-1">
            <Activity size={10} /> Vitals
          </div>
          <div className="grid grid-cols-3 gap-x-2 gap-y-0.5 tabular text-slate-700">
            <div><span className="text-slate-400">BP</span> {vitals.sbp || "—"}/{vitals.dbp || "—"}</div>
            <div><span className="text-slate-400">BMI</span> {vitals.bmi || "—"}</div>
            <div><span className="text-slate-400">HR</span> {vitals.pulse || "—"}</div>
            <div><span className="text-slate-400">RBS</span> {vitals.random_sugar || "—"}</div>
            <div><span className="text-slate-400">FBS</span> {vitals.fasting_sugar || "—"}</div>
            <div><span className="text-slate-400">SpO₂</span> {vitals.spo2 || "—"}</div>
          </div>
        </div>

        {/* Last labs — top 10 canonical results with their date, colour-coded
            by ref-range. Data comes from /patients/{pid}/latest-investigations
            (the same source that powers the AI-extract trend chips). */}
        <LastLabsBlock latestLabs={latestLabs} />

        <div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5 flex items-center gap-1">
            <Pill size={10} /> Medicines ({prevMeds.length})
          </div>
          {prevMeds.length === 0 ? (
            <div className="text-slate-500 italic">No medicines recorded.</div>
          ) : (
            <ul className="space-y-1">
              {prevMeds.map((m, i) => {
                const s = medStatus(m, currentMedicines);
                return (
                  <li key={i} className="border border-slate-100 rounded px-2 py-1.5" data-testid={`last-med-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-slate-900 truncate">
                          {m.brand_name || m.generic_name || "—"}
                          {m.strength && <span className="text-slate-500 font-normal"> · {m.strength}</span>}
                        </div>
                        <div className="text-[10px] text-slate-500 tabular">
                          {m.dose || "—"} · {m.frequency || "—"} · {m.duration || "—"}
                        </div>
                        {s.kind === "changed" && s.cur && (
                          <div className="text-[10px] text-amber-700 mt-0.5">
                            Now: {s.cur.dose || "—"} · {s.cur.frequency || "—"} · {s.cur.duration || "—"}
                          </div>
                        )}
                      </div>
                      <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded shrink-0 ${s.cls}`} data-testid={`last-med-status-${i}`}>
                        {s.label}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {newMeds.length > 0 && (
            <div className="mt-2 pt-2 border-t border-slate-100">
              <div className="text-[9px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Added this visit ({newMeds.length})</div>
              <ul className="space-y-0.5">
                {newMeds.map((m, i) => (
                  <li key={i} className="flex items-center gap-1">
                    <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-600 text-white">NEW</span>
                    <span className="text-slate-800">{m.brand_name || m.generic_name}</span>
                    <span className="text-slate-500 text-[10px]">{m.dose} · {m.frequency}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {visit.advice && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">Advice</div>
            <div className="text-slate-700 line-clamp-3">{visit.advice}</div>
          </div>
        )}
        {visit.next_investigations && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5 flex items-center gap-1"><Calendar size={10} /> Tests advised at last visit</div>
            <div className="flex items-start gap-1.5">
              <div className="text-slate-700 flex-1">{visit.next_investigations}</div>
              {onCopyNextTests && (
                <button
                  type="button"
                  onClick={() => onCopyNextTests(visit.next_investigations)}
                  className="text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 shrink-0"
                  data-testid="last-visit-copy-next-tests"
                  title="Add these tests to today's Investigations table"
                >
                  <Copy size={10} /> Add
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── LastLabsBlock ────────────────────────────────────────────────────
// Compact list of the 10 clinically-important lab values with test date +
// unit + a colour dot indicating whether the result sits inside the
// reference range. Uses the same source as the AI-extract trend chips
// (latestLabs) so no additional network call is needed.
//
// Each row also shows a delta chip (↑ +0.4 vs previous, coloured red when
// the change is clinically worsening and green when it is improving) so
// the doctor can spot regressions at a glance without opening the graph.
const LAB_ORDER = [
  // `betterDown` = true → a rising value is a regression (colour red on ↑).
  //                false → a falling value is a regression (e.g. HDL, eGFR).
  { key: "fbs",           label: "FBS",         betterDown: true  },
  { key: "ppbs",          label: "PPBS",        betterDown: true  },
  { key: "hba1c",         label: "HbA1c",       betterDown: true  },
  { key: "creatinine",    label: "Creatinine",  betterDown: true  },
  { key: "egfr",          label: "eGFR",        betterDown: false },
  { key: "ldl",           label: "LDL",         betterDown: true  },
  { key: "hdl",           label: "HDL",         betterDown: false },
  { key: "tc",            label: "Total Chol.", betterDown: true  },
  { key: "triglycerides", label: "Triglycerides", betterDown: true },
  { key: "tsh",           label: "TSH",         betterDown: true  },
];

// Very lightweight ref-range parser — returns "high" / "low" / "ok" / null.
// Handles the shapes we actually store: "70-100", "< 140", ">= 40", "≤5.7".
function evalRefRange(value, ref) {
  if (value == null || !ref) return null;
  const s = String(ref).replace(/[≤≥]/g, (c) => (c === "≤" ? "<=" : ">="))
                       .replace(/\s+/g, "").toLowerCase();
  const num = (x) => {
    const n = parseFloat(x);
    return Number.isFinite(n) ? n : null;
  };
  // range like "70-100"
  const m = s.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/);
  if (m) {
    const lo = num(m[1]); const hi = num(m[2]);
    if (lo != null && hi != null) {
      if (value < lo) return "low";
      if (value > hi) return "high";
      return "ok";
    }
  }
  // upper cap ("<140" or "<=140")
  const up = s.match(/^<=?\s*(\d+(?:\.\d+)?)$/);
  if (up) {
    const v = num(up[1]);
    if (v != null) return value > v ? "high" : "ok";
  }
  // lower cap (">40" or ">=40")
  const lo = s.match(/^>=?\s*(\d+(?:\.\d+)?)$/);
  if (lo) {
    const v = num(lo[1]);
    if (v != null) return value < v ? "low" : "ok";
  }
  return null;
}

// Threshold for "meaningful" change per canonical key — below this, we
// render a muted "no change" chip instead of a red/green one.
const DELTA_EPSILON = {
  hba1c: 0.1, fbs: 5, ppbs: 5, ldl: 5, hdl: 3, tc: 5, triglycerides: 8,
  creatinine: 0.05, egfr: 2, tsh: 0.2,
};

function DeltaChip({ labKey, row, betterDown }) {
  const d = row?.delta;
  if (d == null || row?.previous == null) return null;
  const eps = DELTA_EPSILON[labKey] ?? 0.5;
  const isNoise = Math.abs(d) < eps;
  const rising = d > 0;
  const worsening = betterDown ? rising : !rising;
  // Icon + tone
  let Icon = Minus, tone = "text-slate-400 bg-slate-50 border-slate-200";
  if (!isNoise) {
    Icon = rising ? TrendingUp : TrendingDown;
    tone = worsening
      ? "text-rose-700 bg-rose-50 border-rose-200"
      : "text-emerald-700 bg-emerald-50 border-emerald-200";
  }
  const sign = d > 0 ? "+" : "";
  const label = isNoise ? "≈" : `${sign}${d}`;
  const pct = row?.delta_pct;
  const title = row?.previous_date
    ? `Previous: ${row.previous} on ${new Date(row.previous_date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" })}${pct != null ? ` · ${pct}%` : ""}`
    : "";
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1 py-0 rounded border text-[9px] font-medium tabular ${tone}`}
      data-testid={`lab-delta-${labKey}`}
      title={title}
    >
      <Icon size={8} strokeWidth={2.5} /> {label}
    </span>
  );
}

function LastLabsBlock({ latestLabs }) {
  const rows = LAB_ORDER
    .map((meta) => ({ ...meta, row: latestLabs?.[meta.key] }))
    .filter((r) => r.row && r.row.value != null);
  if (!rows.length) return null;
  const fmtDate = (d) => {
    try { return new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" }); }
    catch { return d || "—"; }
  };
  return (
    <div data-testid="last-labs-block">
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5 flex items-center gap-1">
        <FlaskConical size={10} /> Last labs ({rows.length})
      </div>
      <ul className="space-y-0.5">
        {rows.map(({ key, label, row, betterDown }) => {
          const state = evalRefRange(row.value, row.ref_range);
          const dot = state === "high" ? "bg-rose-500" : state === "low" ? "bg-amber-500" : state === "ok" ? "bg-emerald-500" : "bg-slate-300";
          const tone = state === "high" ? "text-rose-700" : state === "low" ? "text-amber-700" : state === "ok" ? "text-emerald-700" : "text-slate-800";
          return (
            <li key={key} className="flex items-center gap-1.5 tabular" data-testid={`last-lab-${key}`}>
              <span className={`inline-block h-1.5 w-1.5 rounded-full shrink-0 ${dot}`} title={state || ""} />
              <span className="text-slate-500 w-20 shrink-0">{label}</span>
              <span className={`font-semibold ${tone}`}>{row.value}</span>
              <span className="text-[9px] text-slate-400">{row.unit || ""}</span>
              <DeltaChip labKey={key} row={row} betterDown={betterDown} />
              <span className="ml-auto text-[9px] text-slate-400">{fmtDate(row.date)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
